/**
 * @file OptionsListManager.tsx
 *
 * Generic card for one Shipping overview section (custom zones or pickup
 * locations): a duotone Gravity tile, a title/description, an "Add option" CTA,
 * and — when rows exist — a reorderable {@link DraggableDataTable}. Mirrors
 * WSB-v2's `OptionsListManager`; the data, persistence, and confirm dialogs live
 * in the section components that own this card. Pure Gravity utilities.
 */

import type { ReactNode } from 'react';
import { Button, ContentContainer, Icon, Tile } from '@group.one/react-gravity';
import type { TileName } from '@group.one/gravity';
import { useMediaQuery } from '../hooks/use-media-query';
import {
  DraggableDataTable,
  type DraggableDataTableAction,
  type DraggableDataTableColumn,
} from '../components/DraggableDataTable';

interface OptionsListManagerProps<T extends { id: string }> {
  /** `data-testid` namespace for the section (e.g. `shipping-zones`). */
  testId: string;
  /** Gravity duotone tile name (e.g. `shipping-v3`, `shop`). */
  tileName: TileName;
  title: string;
  description: string;
  addButtonLabel: string;
  dragHandleAriaLabel: string;
  headerColumns: Array<DraggableDataTableColumn<T>>;
  rows: T[];
  actions?: Array<DraggableDataTableAction<T>>;
  onAdd: () => void;
  /** Receives the next ordered array after a drag. */
  onReorder: (rows: T[]) => void;
  getPreviewLabel?: (row: T) => ReactNode;
}

/**
 * Renders a titled shipping section card with an add CTA and a reorderable table.
 *
 * @param props - The section copy, tile, columns, rows, row actions, and handlers.
 *
 * @returns The section card.
 */
export function OptionsListManager<T extends { id: string }>({
  testId,
  tileName,
  title,
  description,
  addButtonLabel,
  dragHandleAriaLabel,
  headerColumns,
  rows,
  actions,
  onAdd,
  onReorder,
  getPreviewLabel,
}: OptionsListManagerProps<T>) {
  const isMobile = useMediaQuery('Mobile');

  const addButton = (
    <Button
      variant="secondary"
      condensed
      className={`gv-flex-shrink-0${isMobile ? ' gv-w-full gv-mt-md' : ''}`}
      data-testid={`${testId}-add`}
      onClick={onAdd}
    >
      <Icon name="add" />
      <span>{addButtonLabel}</span>
    </Button>
  );

  return (
    <section data-testid={testId}>
      <ContentContainer className="gv-p-lg">
        <div className="gv-flex gv-gap-md gv-justify-between">
          <div className="gv-flex gv-flex-col gv-gap-md gv-w-full gv-tab-flex-row">
            <Tile name={tileName} type="duotone" size="sm" className="gv-flex-shrink-0" />
            <div>
              <div className="gv-flex gv-flex-col gv-gap-sm">
                <span className="gv-text-md gv-text-bold">{title}</span>
                <span className="gv-text-sm">{description}</span>
              </div>
              {isMobile && addButton}
            </div>
          </div>
          {!isMobile && addButton}
        </div>

        {rows.length > 0 && (
          <div className="gv-mt-md">
            <DraggableDataTable<T>
              testIdPrefix={testId}
              headerColumns={headerColumns}
              rows={rows}
              actions={actions}
              onReorder={onReorder}
              dragHandleAriaLabel={dragHandleAriaLabel}
              getPreviewLabel={getPreviewLabel}
            />
          </div>
        )}
      </ContentContainer>
    </section>
  );
}
